import { type FC, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import {
  EnrollmentTaxFilingStatusType,
  useGetEnrollmentFilingStatusData,
  useSubmitEnrollmentFilingStatusData,
} from '@egw/api'
import { formatInputToCurrency, removeCurrencySymbols } from '@egw/utils'
import { Input, Button, GenericError, RadioGroup, ButtonVariant, Field } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import { ROUTES } from '../../../constants'
import { PageHeader } from '../../../components'

const log = new Logger('Enrollment - Filing Status')

interface FormData {
  taxFilingStatus: string
  personalAnnualGrossIncome: string
}

export const FilingStatusPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const [isOpenIncomeField, setIsOpenIncomeField] = useState(false)

  const { data, isLoading, error } = useGetEnrollmentFilingStatusData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentFilingStatusData()

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    watch,
    formState: { isValid },
  } = useForm<FormData>({
    defaultValues: {
      taxFilingStatus: '',
      personalAnnualGrossIncome: '',
    },
  })

  const personalAnnualGrossIncome = watch('personalAnnualGrossIncome')

  useEffect(() => {
    // Get data from userInfo
    if (data?.enrollmentData?.userInfo) {
      reset({
        personalAnnualGrossIncome: data.enrollmentData.userInfo.income?.toString(),
        taxFilingStatus: data.enrollmentData.userInfo.taxFilingStatus,
      })
    }
    if (data) {
      if (data.path && data.path !== `${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.INVESTMENTS_FILING_STATUS.path}`) {
        // User should be redirected to another page
        navigate(data.path)
      }
    }
  }, [data, reset])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData

    try {
      const response = await mutate({
        investmentProfile: {
          taxFilingStatus: formData.taxFilingStatus as EnrollmentTaxFilingStatusType,
        },
        personalAnnualGrossIncome: removeCurrencySymbols(formData.personalAnnualGrossIncome.toString()) || 0,
      })
      if (response?.path) {
        navigate(response.path)
      } else {
        // TODO: Handle the case when path is not returned
        navigate(ROUTES.HOME.path)
      }
    } catch (e) {
      log.error('Error submitting email', e)
    }
  })

  return (
    <EnrollmentLayout title="Filing Status">
      <div className="egw-container-enroll h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError ? (
          <>
            <PageHeader title={t('investments.filing-status.title')} />
            <p className="typo-primary-sm-regular text-text-meta">{t('investments.filing-status.description')}</p>
            <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col h-full">
              <p className="typo-primary-base-semibold my-md">
                {t('investments.filing-status.your_tax_filing_status')}
              </p>
              <Controller
                name="taxFilingStatus"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { ref, ...restField } }) => (
                  <RadioGroup
                    {...restField}
                    options={Object.values(EnrollmentTaxFilingStatusType).map((item) => ({
                      value: item,
                      label: t(`investments.filing-status.tax_filing_status.${item}`),
                    }))}
                    name="taxFilingStatus"
                    className="pb-md"
                    testId="tax-filing-status-radio"
                  />
                )}
              />
              <hr className="mb-md" />
              <p className="typo-primary-base-semibold text-text-meta mb-md">
                {t('investments.filing-status.confirm_income_title')}
              </p>
              <p className="typo-primary-sm-regular text-text-meta mb-md">
                {t('investments.filing-status.confirm_income_p1')}
              </p>
              <p className="typo-primary-sm-regular text-text-meta mb-md">
                {t('investments.filing-status.confirm_income_p2')}
              </p>
              <p className="typo-primary-sm-regular text-text-meta mb-md">
                {t('investments.filing-status.confirm_income_p3')}
              </p>

              {isOpenIncomeField ? (
                <div className="bg-background-light px-xs py-sm">
                  <p className="typo-primary-sm-semibold mb-xs">{t('investments.filing-status.your_annual_income')}</p>
                  <Controller
                    name="personalAnnualGrossIncome"
                    control={control}
                    rules={{
                      required: true,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={formatInputToCurrency(field.value, true)}
                        label={t('investments.filing-status.household_income')}
                        name="personalAnnualGrossIncome"
                        className="mb-sm"
                        testId="personal-annual-gross-income-radio"
                        inputMode="decimal"
                      />
                    )}
                  />
                  <Button
                    onClick={() => {
                      setIsOpenIncomeField(false)
                    }}
                    isDisabled={!personalAnnualGrossIncome}
                    variant={ButtonVariant.Secondary}
                    className="w-full"
                    testId="personal-annual-gross-income-update-btn"
                  >
                    {t('common.update')}
                  </Button>
                </div>
              ) : null}

              {!isOpenIncomeField && (
                <Field
                  valueClassName="text-text-meta typo-primary-base-regular"
                  value={t('investments.filing-status.annual_income')}
                  rightValue={formatInputToCurrency(getValues('personalAnnualGrossIncome').toString(), true)}
                  className="py-2xs"
                  testId="personalAnnualGrossIncome"
                  onEdit={() => {
                    setIsOpenIncomeField(true)
                  }}
                />
              )}

              <div className="mt-auto md:mt-lg">
                <Button
                  type="submit"
                  className="w-full md:w-min mt-lg"
                  isDisabled={!isValid || isOpenIncomeField}
                  isLoading={isSubmitting}
                  testId="enrollment-continue-button"
                >
                  {t('common.continue')}
                </Button>
              </div>
            </form>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentLayout>
  )
}
